/** Select presentation for moving one player to a formation slot or Unassigned. */
import { ChevronDownIcon } from "lucide-react-native";
import React, { memo } from "react";

import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectScrollView,
  SelectTrigger,
} from "@/components/atoms/Select";
import { TEXT_VARIANT_CLASS } from "@/components/atoms/types";
import type { TacticalSlot } from "@/generated/graphql";

/** Where the Select sits; each surface owns a unique test identity. */
export type PitchAssignmentSurface = "members" | "pitch" | "unassigned";

/** Prepared props for the assignment Select presentation. */
export interface PitchAssignmentSelectViewProps {
  readonly surface: PitchAssignmentSurface;
  readonly playerId: string;
  readonly accessibleLabel: string;
  readonly value: TacticalSlot | null;
  readonly slots: readonly TacticalSlot[];
  /** Trigger text while no slot is selected; "Unassigned" by default. */
  readonly triggerLabel?: string;
  /** Whether the Unassigned option is offered (SE-8391: not on the panel). */
  readonly includeUnassigned?: boolean;
  /** Read-only trigger for a surface that cannot write a placement yet. */
  readonly isDisabled?: boolean;
  /**
   * Trigger layout per surface (SE-8442). The full trigger takes its width
   * from the input's intrinsic size (160 px): `compact` is the chevron-only
   * trigger for the pitch card, which already names its slot and gave the
   * player's name 0 px; `narrow` caps the members-panel trigger at `w-20` so
   * the 280 px row keeps room for the name.
   */
  readonly layout?: PitchAssignmentTriggerLayout;
  readonly onChange: (value: string) => void;
}

/** The three trigger layouts; see {@link PitchAssignmentSelectViewProps.layout}. */
export type PitchAssignmentTriggerLayout = "full" | "narrow" | "compact";

/**
 * Trigger classes per layout; the compact one matches the drag handle. Spelled
 * `h-5 w-5` rather than the shorthand because the trigger's `size="sm"` variant
 * carries `h-9`, and only an explicit `h-*` displaces it (measured 20 × 36 with
 * the shorthand).
 */
const TRIGGER_CLASS: Readonly<Record<PitchAssignmentTriggerLayout, string>> = {
  full: "h-7 min-w-16 rounded-sm border-outline-default px-1",
  narrow: "h-7 w-20 rounded-sm border-outline-default px-1",
  compact: "h-5 w-5 justify-center rounded-sm border-0 p-0",
};

/**
 * Trigger text per layout. The full trigger is the Unassigned panel's
 * `Place ▾`, which the frame (971:20747, SE-8486 row W80) draws caption-strong
 * in the accent; the members trigger keeps its micro; the compact one hides
 * its input (it still feeds the native select's aria-label on web).
 */
const INPUT_CLASS: Readonly<Record<PitchAssignmentTriggerLayout, string>> = {
  full: `${TEXT_VARIANT_CLASS["caption-strong"]} text-accent-primary`,
  narrow: "text-micro",
  compact: "hidden",
};

/** One Select option. */
export interface PitchAssignmentOption {
  readonly label: string;
  readonly value: string;
}

/**
 * The options the Select offers: every slot, then Unassigned when wanted.
 * @param slots - Slots offered in the current formation
 * @param includeUnassigned - Whether the Unassigned option closes the list
 * @returns Options in menu order
 */
export const assignmentOptions = (
  slots: readonly TacticalSlot[],
  includeUnassigned: boolean
): readonly PitchAssignmentOption[] => [
  ...slots.map(slot => ({ label: slot, value: slot })),
  ...(includeUnassigned ? [{ label: "Unassigned", value: "UNASSIGNED" }] : []),
];

/**
 * The Select's selected value, always one of its options or empty (SE-8670).
 * On web the Select is a native `<select>` that renders its disabled
 * placeholder option only while the value is empty; a value no option carries
 * — "UNASSIGNED" on the Unassigned panel, which offers no Unassigned — makes
 * the browser pre-select the first slot, and picking that slot fires no change.
 * @param value - Current tactical slot, or null for Unassigned
 * @param includeUnassigned - Whether the Unassigned option is offered
 * @returns The value to select, or null for the placeholder
 */
export const selectedAssignmentValue = (
  value: TacticalSlot | null,
  includeUnassigned: boolean
): string | null => value ?? (includeUnassigned ? "UNASSIGNED" : null);

/**
 * Renders a compact Select whose native trigger supports keyboard navigation.
 * @param props - Player label, selected slot, options, and change callback.
 * @param props.playerId - Player identity used by stable test IDs.
 * @param props.surface - Surface used to disambiguate IDs and accessible names.
 * @param props.accessibleLabel - Accessible name prepared by the container.
 * @param props.value - Current tactical slot or null for Unassigned.
 * @param props.slots - Slots offered in the current formation.
 * @param props.triggerLabel - Trigger text while nothing is selected.
 * @param props.includeUnassigned - Whether the Unassigned option is offered.
 * @param props.isDisabled - Whether the trigger is read-only.
 * @param props.layout - Trigger layout: full, narrow (members panel) or compact (pitch card).
 * @param props.onChange - Receives a selected Select value.
 */
const PitchAssignmentSelectView = ({
  playerId,
  surface,
  accessibleLabel,
  value,
  slots,
  triggerLabel = "Unassigned",
  includeUnassigned = true,
  isDisabled = false,
  layout = "full",
  onChange,
}: PitchAssignmentSelectViewProps) => (
  <Select
    selectedValue={selectedAssignmentValue(value, includeUnassigned)}
    onValueChange={onChange}
    isDisabled={isDisabled}
  >
    <SelectTrigger
      testID={`pitch-assignment-select-${surface}-${playerId}`}
      accessibilityLabel={accessibleLabel}
      variant="outline"
      size="sm"
      className={TRIGGER_CLASS[layout]}
    >
      {/* Mounted even when compact: on web it feeds the native select's
          aria-label (the atom sets it from this placeholder). */}
      <SelectInput
        value={value ?? triggerLabel}
        placeholder={accessibleLabel}
        className={INPUT_CLASS[layout]}
      />
      <SelectIcon as={ChevronDownIcon} />
    </SelectTrigger>
    <SelectPortal>
      <SelectBackdrop />
      <SelectContent>
        <SelectScrollView>
          {assignmentOptions(slots, includeUnassigned).map(option => (
            <SelectItem
              key={option.value}
              label={option.label}
              value={option.value}
            />
          ))}
        </SelectScrollView>
      </SelectContent>
    </SelectPortal>
  </Select>
);

PitchAssignmentSelectView.displayName = "PitchAssignmentSelectView";
export default memo(PitchAssignmentSelectView);
